import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_CORPORATE_CODE, EMPTY_CORPORATE_CODE_LIST_CONFIGURATION, EMPTY_CORPORATE_CODE_LIST_CRITERIA } from "../constant/corporateCodeConstant";
import { CorporateCode } from "../objects/corporateCode";
import { CorporateCodeListCriteria } from "../objects/corporateCodeListCriteria";
import { CorporateCodeState } from "../objects/corporateCodeState";
import { setCorporateCodePreference } from "../utils/setCorporateCodePreference";
import { Column } from "../objects/column";
import { ClientBranchCompany } from "../objects/company";


const initialState: CorporateCodeState = {
    corporate_code_list: [],
    corporate_code_list_configuration: EMPTY_CORPORATE_CODE_LIST_CONFIGURATION,
    corporate_code_list_loading: false,
    corporate_code_list_count: 0,
    corporate_code_list_criteria: EMPTY_CORPORATE_CODE_LIST_CRITERIA,
    corporate_code: EMPTY_CORPORATE_CODE,
    first_preference_loaded: false,
    delete_corporate_code: null,
    client_branch: null
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setDeleteCorporateCodeReducer = (state: CorporateCodeState, action: PayloadAction<CorporateCode | null>): CorporateCodeState => ({
    ...state, delete_corporate_code: action.payload
});

const addCorporateCodeListConfigurationPreferenceReducer = (state: CorporateCodeState, action: PayloadAction<Preferences[]>): CorporateCodeState => ({
    ...state, corporate_code_list_configuration: { ...state.corporate_code_list_configuration, preferences: [...state.corporate_code_list_configuration.preferences, ...action.payload] }
});

const setCorporateCodeListConfigurationPreferenceReducer = (state: CorporateCodeState, action: PayloadAction<Preferences[]>): CorporateCodeState => ({
    ...state, corporate_code_list_configuration: { ...state.corporate_code_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: CorporateCodeState, action: PayloadAction<Preferences[]>): CorporateCodeState => {
    const { preferences, columns, order_by, order } = setCorporateCodePreference(action.payload, state.corporate_code_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        corporate_code_list_configuration: { ...state.corporate_code_list_configuration, preferences, columns },
        corporate_code_list_criteria: { ...state.corporate_code_list_criteria, order, order_by }
    };
};

const toggleColumnReducer = (state: CorporateCodeState, action: PayloadAction<number>): CorporateCodeState => {
    const columns = cloneDeep([...state.corporate_code_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, corporate_code_list_configuration: { ...state.corporate_code_list_configuration, columns } };
};

const updateCorporateCodeListReducer = (state: CorporateCodeState, action: PayloadAction<CorporateCode[]>): CorporateCodeState => ({
    ...state, corporate_code_list: action.payload
});
const updateCorporateCodeListCriteriaReducer = (state: CorporateCodeState, action: PayloadAction<CorporateCodeListCriteria>): CorporateCodeState => ({
    ...state, corporate_code_list_criteria: action.payload
});
const updateCorporateCodeReducer = (state: CorporateCodeState, action: PayloadAction<CorporateCode>): CorporateCodeState => ({
    ...state, corporate_code: action.payload
});
const updateCorporateCodeListLoadingReducer = (state: CorporateCodeState, action: PayloadAction<boolean>): CorporateCodeState => ({
    ...state, corporate_code_list_loading: action.payload
});
const updateCorporateCodeListCountReducer = (state: CorporateCodeState, action: PayloadAction<number>): CorporateCodeState => ({
    ...state, corporate_code_list_count: action.payload
});
const setCompanyReducer = (state: CorporateCodeState, action: PayloadAction<ClientBranchCompany | any | null>): CorporateCodeState => ({
    ...state, client_branch: action.payload
});

const setColumnsReducer = (state: CorporateCodeState, action: PayloadAction<Column[]>): CorporateCodeState => ({
    ...state, corporate_code_list_configuration: { ...state.corporate_code_list_configuration, columns: action.payload },
});

export const corporateCode = createSlice({
    name: 'corporate_code',
    initialState,
    reducers: {
        setDeleteCorporateCode: setDeleteCorporateCodeReducer,
        addCorporateCodeListConfigurationPreference: addCorporateCodeListConfigurationPreferenceReducer,
        setCorporateCodeListConfigurationPreference: setCorporateCodeListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateCorporateCodeList: updateCorporateCodeListReducer,
        updateCorporateCodeListCriteria: updateCorporateCodeListCriteriaReducer,
        updateCorporateCode: updateCorporateCodeReducer,
        updateCorporateCodeListLoading: updateCorporateCodeListLoadingReducer,
        updateCorporateCodeListCount: updateCorporateCodeListCountReducer,
        setColumns: setColumnsReducer,
        setCompany: setCompanyReducer,
        resizeColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.corporate_code_list_configuration.columns =
                applyColumnResize(state.corporate_code_list_configuration.columns, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setDeleteCorporateCode,
    addCorporateCodeListConfigurationPreference,
    setCorporateCodeListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateCorporateCodeList,
    updateCorporateCodeListCriteria,
    updateCorporateCode,
    updateCorporateCodeListLoading,
    updateCorporateCodeListCount,
    setCompany,
    resizeColumn
} = corporateCode.actions;